import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { format, addDays, subDays, startOfMonth, endOfMonth, eachDayOfInterval } from 'date-fns';
import { facilityApi, getFacilityClasses, memberApi } from '../api';
import { reservationApi } from '../reservation_api';
import { Court, Class, ScheduleSlot, FacilityEvent, PublicFacility } from '../api_types';
import { courtApi, recurringEventApi, eventApi } from '../api';
import NewCourtReservationModal from '../business/CourtReservationModal';
import { useAuth } from '../contexts/AuthContext';
import './styles/NewMemberCourtReservations.css';
import LoadingSpinner from '../shared/components/LoadingSpinner';
import { websocketService } from '../services/websocket';
import NotificationCounter from '../shared/components/NotificationCounter';
import { useQuery, useQueryClient } from '@tanstack/react-query';

interface BusinessHours {
  [key: string]: {
    isClosed: boolean;
    openTime: string;
    closeTime: string;
  };
  sunday: { isClosed: boolean; openTime: string; closeTime: string };
  monday: { isClosed: boolean; openTime: string; closeTime: string };
  tuesday: { isClosed: boolean; openTime: string; closeTime: string };
  wednesday: { isClosed: boolean; openTime: string; closeTime: string };
  thursday: { isClosed: boolean; openTime: string; closeTime: string };
  friday: { isClosed: boolean; openTime: string; closeTime: string };
  saturday: { isClosed: boolean; openTime: string; closeTime: string };
}

interface Notification {
  id: string;
  message: string;
  timestamp: Date;
}

interface NewMemberCourtReservationsProps {
  facilityId: string;
  businessId: string;
  facility?: PublicFacility;
  onReservationAttempt?: (reservationData: {
    courtId: string;
    date: string;
    startTime: string;
    endTime: string;
    price: number;
  }) => void;
  disableNewReservation?: boolean;
  isGuestMode?: boolean;
  defaultStartTime?: string;
  defaultEndTime?: string;
  onTimeSlotSelect?: (timeSlot: { courtId: string; time: string }) => void;
  forceShowGrid?: boolean;
}

const NewMemberCourtReservations: React.FC<NewMemberCourtReservationsProps> = ({ 
  facilityId,
  businessId,
  facility,
  onReservationAttempt,
  disableNewReservation = false,
  isGuestMode = false,
  defaultEndTime,
  onTimeSlotSelect,
  forceShowGrid = false
}) => {
  const { user } = useAuth();
  const [selectedDate, setSelectedDate] = useState(() => {
    // Get current date in local timezone
    const now = new Date();
    // Set time to midnight to avoid timezone issues
    return new Date(now.getFullYear(), now.getMonth(), now.getDate());
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState<ScheduleSlot | null>(null);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<{
    courtId: string;
    time: string;
  } | null>(null);
  const [showNewReservationModal, setShowNewReservationModal] = useState(false);
  const [selectedDuration, setSelectedDuration] = useState<'30min' | '1hour'>('1hour');
  const [events, setEvents] = useState<FacilityEvent[]>([]);
  const [scrollInitialized, setScrollInitialized] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);
  const [userName, setUserName] = useState('');
  const [businessHours, setBusinessHours] = useState<BusinessHours>({
    sunday: { isClosed: false, openTime: '07:00', closeTime: '21:00' },
    monday: { isClosed: false, openTime: '07:00', closeTime: '21:00' },
    tuesday: { isClosed: false, openTime: '07:00', closeTime: '21:00' },
    wednesday: { isClosed: false, openTime: '07:00', closeTime: '21:00' },
    thursday: { isClosed: false, openTime: '07:00', closeTime: '21:00' },
    friday: { isClosed: false, openTime: '07:00', closeTime: '21:00' },
    saturday: { isClosed: false, openTime: '07:00', closeTime: '21:00' }
  });
  const [isMobile, setIsMobile] = useState(false);
  const [showSmartModal, setShowSmartModal] = useState(true);
  const queryClient = useQueryClient();
  const { data: courts = [], isLoading: courtsLoading } = useQuery<Court[]>({
    queryKey: ['courts', businessId, facilityId],
    queryFn: async () => {
      const data = await courtApi.getCourts(businessId, facilityId);
      return data.sort((a, b) => {
        const aMatch = a.name.match(/\d+/);
        const bMatch = b.name.match(/\d+/);
        const aNum = aMatch ? parseInt(aMatch[0]) : 0;
        const bNum = bMatch ? parseInt(bMatch[0]) : 0;
        if (aNum === bNum) return a.name.localeCompare(b.name);
        return aNum - bNum;
      });
    },
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  // Fetch user data from API
  useEffect(() => {
    const fetchUserData = async () => {
      if (!user?.uid) return;
      
      try {
        const userData = await memberApi.getMemberDetails(businessId, user.uid, facilityId);
        setUserName(`${userData.firstName} ${userData.lastName}`);
      } catch (err) {
        console.error('Error fetching user data:', err);
      }
    };

    fetchUserData();
  }, [user?.uid]);

  // Load business hours
  useEffect(() => {
    const loadBusinessHours = async () => {
      try {
        const businessHours = await facilityApi.getFacilityBusinessHours(businessId, facilityId);
        setBusinessHours(businessHours as BusinessHours);
        
      } catch (err) {
        console.error('Error loading business hours:', err);
      }
    };

    loadBusinessHours();
  }, [facilityId]);

  // Move TIME_SLOTS inside component and make it dependent on business hours
  const TIME_SLOTS = useMemo(() => {
    const slots = [];
    const dayOfWeek = selectedDate.getDay();
    const dayName = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'][dayOfWeek];
    const hours = businessHours[dayName];

    if (hours.isClosed) {
      return []; // Return empty slots if the facility is closed
    }

    const [openHour, openMinute] = hours.openTime.split(':').map(Number);
    const [closeHour, closeMinute] = hours.closeTime.split(':').map(Number);

    for (let hour = openHour; hour <= closeHour; hour++) {
      const startMinute = hour === openHour ? openMinute : 0;
      const endMinute = hour === closeHour ? closeMinute : 60;
      
      for (let minute = startMinute; minute < endMinute; minute += 30) {
        const period = hour >= 12 ? 'PM' : 'AM';
        const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
        slots.push({
          display: `${displayHour}:${minute.toString().padStart(2, '0')} ${period}`,
          value: `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`
        });
      }
    }
    return slots;
  }, [businessHours, selectedDate]);

  // Add React Query for schedules
  const { data: schedules = [], isLoading: schedulesLoading, refetch: refetchSchedules } = useQuery<ScheduleSlot[]>({
    queryKey: ['schedules', businessId, facilityId, format(selectedDate, 'yyyy-MM-dd')],
    queryFn: async () => {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      
      // Fetch regular schedules
      const schedulesData = await reservationApi.getMemberSchedule(facilityId, dateStr, businessId);

      // Fetch classes for the selected date
      const facilityClasses = await getFacilityClasses(businessId, facilityId);
      const classesForDate = facilityClasses.filter(
        (classItem: Class) => classItem.date === dateStr && classItem.status === 'scheduled'
      );
      
      // Convert classes to schedule slots
      const classSlots = classesForDate.flatMap((classItem: Class) => {
        return classItem.courtIds.map((courtId: string) => {
          return {
            id: `${classItem.id}-${courtId}`,
            courtId,
            date: classItem.date,
            startTime: classItem.startTime,
            endTime: classItem.endTime,
            status: 'blocked' as const,
            notes: classItem.description,
            assignedTo: null,
            assignedUserDetails: null,
            isEvent: true,
            isClass: true,
            eventTitle: classItem.title,
            title: classItem.title,
            capacity: classItem.maxCapacity,
            price: classItem.price,
            registeredMembers: classItem.registeredMembers
          };
        });
      });

      // Fetch facility events
      const facilityEventsData = await eventApi.getFacilityEventsForDate(businessId, facilityId, dateStr);

      // Fetch recurring event occurrences for the selected date
      const recurringEventOccurrences = await recurringEventApi.getRecurringEventOccurrencesByDate(businessId, facilityId, dateStr);
      
      // Convert recurring event occurrences to schedule slots
      const recurringEventSlots = recurringEventOccurrences
        .filter(occurrence => occurrence.status === 'scheduled')
        .flatMap(occurrence => {
          return occurrence.courtIds.map(courtId => ({
            id: `${occurrence.parentEventId}-${courtId}-${dateStr}`,
            courtId,
            date: dateStr,
            startTime: occurrence.startTime,
            endTime: occurrence.endTime,
            status: 'blocked' as const,
            notes: occurrence.description,
            assignedTo: null,
            assignedUserDetails: null,
            isEvent: true,
            isRecurring: true,
            eventTitle: occurrence.title,
            title: occurrence.title,
            capacity: occurrence.capacity,
            price: occurrence.price,
            registrationRequired: occurrence.registrationRequired,
            registrationDeadline: occurrence.registrationDeadline
          }));
        });

      // Convert facility events to schedule slots
      const eventSlots = facilityEventsData
        .filter(event => event.status === 'scheduled' && event.date === dateStr)
        .flatMap(event => {
          return event.courtIds.map(courtId => {
            return {
              id: `${event.id}-${courtId}`,
              courtId,
              date: event.date,
              startTime: event.startTime,
              endTime: event.endTime,
              status: 'blocked' as const,
              notes: event.description,
              assignedTo: null,
              assignedUserDetails: null,
              isEvent: true,
              isRecurring: false,
              eventTitle: event.title,
              title: event.title
            };
          });
        });

      setEvents(facilityEventsData);
      return [...schedulesData, ...classSlots, ...eventSlots, ...recurringEventSlots] as ScheduleSlot[];
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
  });

  // Update WebSocket handler to use React Query cache
  useEffect(() => {
    if (facilityId) {
      const handleCourtUpdate = (data: any) => {
        if (data.action === 'created' || data.action === 'updated' || data.action === 'deleted') {
          // Create notification message
          const court = courts.find(c => c.id === data.reservation.courtId);
          const courtName = court?.name || 'a court';
          const isYou = data.reservation.assignedUserDetails?.id === user?.uid;
          const userName = isYou ? 'You' : (data.reservation.assignedUserDetails?.name || 'Someone');
          const time = new Date(data.reservation.date + 'T' + data.reservation.startTime).toLocaleTimeString();
          
          let message = '';
          if (data.action === 'created') {
            message = `${userName} booked ${courtName} at ${time}`;
          } else if (data.action === 'updated') {
            message = `${userName} updated their booking for ${courtName} at ${time}`;
          } else {
            message = `${userName} cancelled their booking for ${courtName} at ${time}`;
          }
          
          addNotification(message);

          // Update React Query cache
          queryClient.setQueryData(['schedules', businessId, facilityId, data.reservation.date], (oldData: ScheduleSlot[] = []) => {
            const newData = [...oldData];
            
            if (data.action === 'created' || data.action === 'updated') {
              const index = newData.findIndex(s => 
                s.courtId === data.reservation.courtId && 
                s.date === data.reservation.date &&
                s.startTime === data.reservation.startTime
              );
              
              if (index !== -1) {
                newData[index] = {
                  ...newData[index],
                  ...data.reservation,
                  className: 'schedule-slot-update'
                };
              } else {
                newData.push({
                  ...data.reservation,
                  className: 'schedule-slot-new'
                });
              }
            } else if (data.action === 'deleted') {
              const index = newData.findIndex(s => 
                s.courtId === data.reservation.courtId && 
                s.date === data.reservation.date &&
                s.startTime === data.reservation.startTime
              );
              
              if (index !== -1) {
                newData[index] = {
                  ...newData[index],
                  className: 'schedule-slot-delete'
                };
                setTimeout(() => {
                  queryClient.setQueryData(['schedules', businessId, facilityId, data.reservation.date], 
                    (currentData: ScheduleSlot[] = []) => currentData.filter((_, i) => i !== index)
                  );
                }, 300);
              }
            }
            
            return newData;
          });
        }
      };

      websocketService.subscribe('court_update', handleCourtUpdate);
      return () => {
        websocketService.unsubscribe('court_update', handleCourtUpdate);
      };
    }
  }, [facilityId, courts, user?.uid, queryClient]);

  // Add CSS for transitions
  useEffect(() => {
    const styles = `
      .schedule-slot {
        transition: all 0.3s ease-in-out;
      }
      
      .schedule-slot-update {
        animation: updatePulse 0.3s ease-in-out;
      }
      
      .schedule-slot-new {
        animation: newPulse 0.3s ease-in-out;
      }
      
      .schedule-slot-delete {
        animation: deletePulse 0.3s ease-in-out;
      }
      
      @keyframes updatePulse {
        0% { background-color: rgba(147, 51, 234, 0.2); }
        100% { background-color: transparent; }
      }
      
      @keyframes newPulse {
        0% { background-color: rgba(34, 197, 94, 0.2); }
        100% { background-color: transparent; }
      }
      
      @keyframes deletePulse {
        0% { background-color: rgba(239, 68, 68, 0.2); }
        100% { background-color: transparent; }
      }
    `;

    const styleSheet = document.createElement("style");
    styleSheet.innerText = styles;
    document.head.appendChild(styleSheet);
    return () => {
      document.head.removeChild(styleSheet);
    };
  }, []);

  const getSlotStatus = useCallback((courtId: string, time: string) => {
    const slot = schedules.find(s => {
      // Convert times to minutes since midnight for easier comparison
      const slotTime = parseInt(time.split(':')[0]) * 60 + parseInt(time.split(':')[1]);
      const startTime = parseInt(s.startTime.split(':')[0]) * 60 + parseInt(s.startTime.split(':')[1]);
      const endTime = parseInt(s.endTime.split(':')[0]) * 60 + parseInt(s.endTime.split(':')[1]);
      
      // Check if the slot overlaps with any event or reservation
      const hasTimeConflict = slotTime >= startTime && slotTime < endTime;
      return s.courtId === courtId && hasTimeConflict;
    });

    if (!slot) {
      return { status: 'available', details: null, reservation: null, isStart: false, isMiddle: false, isEnd: false };
    }

    // Convert times to minutes for comparison
    const slotTime = parseInt(time.split(':')[0]) * 60 + parseInt(time.split(':')[1]);
    const startTime = parseInt(slot.startTime.split(':')[0]) * 60 + parseInt(slot.startTime.split(':')[1]);
    const endTime = parseInt(slot.endTime.split(':')[0]) * 60 + parseInt(slot.endTime.split(':')[1]);

    const formatTime = (time: string) => {
      const [hours, minutes] = time.split(':');
      const hour = parseInt(hours);
      const period = hour >= 12 ? 'PM' : 'AM';
      const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
      return `${displayHour}:${minutes} ${period}`;
    };

    let displayName: string;

    if (slot.isEvent) {
      // Events get their own title
      displayName = slot.eventTitle || 'Event';
    } else {
      // Reservations: use explicit reservationName, then user's name, then generic text
      displayName = slot.reservationName
        || slot.assignedUserDetails?.name
        || 'Reserved';
    }

    const details = {
      name: displayName,
      time: `${formatTime(slot.startTime)} - ${formatTime(slot.endTime)}`,
      isRecurring: slot.isRecurring
    };

    return {
      status: slot.status,
      details,
      reservation: slot,
      isStart: slotTime === startTime,
      isMiddle: slotTime > startTime && slotTime < endTime - 1,
      isEnd: slotTime === endTime - 1,
      isEvent: slot.isEvent,
      isRecurring: slot.isRecurring,
      isOwnReservation: slot.assignedTo === user?.uid
    };
  }, [schedules, user, userName]);

  const calculatePrice = (startTime: string, date: string): number => {
    if (!facility) return 0;

    const hour = parseInt(startTime.split(':')[0]);
    const bookingDate = new Date(date);
    const isWeekend = bookingDate.getDay() === 0 || bookingDate.getDay() === 6;
    
    const peakStart = parseInt(facility.publicPricing.peakHours.startTime.split(':')[0]);
    const peakEnd = parseInt(facility.publicPricing.peakHours.endTime.split(':')[0]);
    const isPeakHour = !isWeekend && hour >= peakStart && hour < peakEnd;
    
    if (isWeekend) {
      return facility.publicPricing.weekendRate;
    } else if (isPeakHour) {
      return facility.publicPricing.weekdayPeakRate;
    } else {
      return facility.publicPricing.weekdayOffPeakRate;
    }
  };

  const handleSlotClick = (courtId: string, time: string, existingReservation?: ScheduleSlot) => {
    if (existingReservation) {
      // For guest mode, don't allow clicking on any reservations
      if (isGuestMode) {
        return;
      }
      
      // For members, only allow editing their own reservations
      if (!isGuestMode && existingReservation.assignedTo === user?.uid) {
        setSelectedSchedule(existingReservation);
        setIsModalOpen(true);
      }
      return;
    }

    // Calculate price for the time slot
    const price = calculatePrice(time, format(selectedDate, 'yyyy-MM-dd'));

    if (onReservationAttempt) {
      // For guest bookings
      onReservationAttempt({
        courtId,
        date: format(selectedDate, 'yyyy-MM-dd'),
        startTime: time,
        endTime: defaultEndTime || '', // This will be set by the time selection dialog
        price
      });
    } else if (onTimeSlotSelect) {
      // For guest time slot selection
      onTimeSlotSelect({ courtId, time });
    } else {
      // For member bookings
      setSelectedTimeSlot({ courtId, time });
      setShowNewReservationModal(true);
    }
  };

  const getRecommendedTimeSlots = useCallback((courtId: string) => {
    const recommendations: { startTime: string; duration: '30min' | '1hour'; isRecommended: boolean }[] = [];
    const currentTime = new Date();
    const dayOfWeek = currentTime.getDay();
    const dayName = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'][dayOfWeek];
    const hours = businessHours[dayName];

    if (hours.isClosed) {
      return []; // No recommendations if facility is closed
    }

    const [openHour, openMinute] = hours.openTime.split(':').map(Number);
    const [closeHour, closeMinute] = hours.closeTime.split(':').map(Number);
    
    let startHour = currentTime.getHours();
    let startMinute = currentTime.getMinutes();
    
    // If current time is before opening, start at opening time
    if (startHour < openHour || (startHour === openHour && startMinute < openMinute)) {
      startHour = openHour;
      startMinute = openMinute;
    }
    
    // If current time is after closing, no recommendations
    if (startHour > closeHour || (startHour === closeHour && startMinute >= closeMinute)) {
      return [];
    }

    // Round to next 30-minute interval
    startMinute = Math.ceil(startMinute / 30) * 30;
    if (startMinute >= 60) {
      startHour += 1;
      startMinute = 0;
    }

    // Generate up to 8 recommendations within business hours
    for (let i = 0; i < 8; i++) {
      // Check if we've reached closing time
      if (startHour > closeHour || (startHour === closeHour && startMinute >= closeMinute)) {
        break;
      }

      const time = `${startHour.toString().padStart(2, '0')}:${startMinute.toString().padStart(2, '0')}`;
      const { status } = getSlotStatus(courtId, time);
      
      if (status === 'available') {
        recommendations.push({
          startTime: time,
          duration: '1hour',
          isRecommended: true
        });
      }
      
      startMinute += 30;
      if (startMinute >= 60) {
        startHour += 1;
        startMinute = 0;
      }
    }
    
    return recommendations;
  }, [getSlotStatus, businessHours]);

  useEffect(() => {
    if (!schedulesLoading && !scrollInitialized) {
      // Get current time in PST/PDT
      const now = new Date();
      // Convert to PST/PDT by getting the local time in that timezone
      const pstOffset = -7; // PST/PDT offset from UTC
      const utc = now.getTime() + (now.getTimezoneOffset() * 60000);
      const pstTime = new Date(utc + (3600000 * pstOffset));
      
      const currentHour = pstTime.getHours();
      const currentMinute = pstTime.getMinutes();
      
      // Round to nearest hour
      const roundedHour = Math.round(currentHour + currentMinute/60);
      
      // Find the time slot element and scroll to it
      const timeSlotElement = document.querySelector(`[data-time="${roundedHour.toString().padStart(2, '0')}:00"]`);
      if (timeSlotElement) {
        timeSlotElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      
      setScrollInitialized(true);
    }
  }, [schedulesLoading, scrollInitialized]);

  // Add scroll event listener to prevent scrolling to past time slots
  useEffect(() => {
    const handleScroll = () => {
      const gridContainer = document.querySelector('.scheduler-grid-container');
      if (!gridContainer) return;

      const now = new Date();
      const pstOffset = -7; // PST/PDT offset from UTC
      const utc = now.getTime() + (now.getTimezoneOffset() * 60000);
      const pstTime = new Date(utc + (3600000 * pstOffset));
      
      const currentHour = pstTime.getHours();
      const currentMinute = pstTime.getMinutes();
      const currentTimeInMinutes = currentHour * 60 + currentMinute;

      // Find the first visible time slot
      const timeSlots = Array.from(document.querySelectorAll('.scheduler-time-slot'));
      let firstVisibleTimeSlot = null;
      
      for (const slot of timeSlots) {
        const rect = slot.getBoundingClientRect();
        if (rect.top >= 0) {
          firstVisibleTimeSlot = slot;
          break;
        }
      }

      if (firstVisibleTimeSlot) {
        const timeStr = firstVisibleTimeSlot.getAttribute('data-time');
        if (timeStr) {
          const [hours, minutes] = timeStr.split(':').map(Number);
          const slotTimeInMinutes = hours * 60 + minutes;
          
          // If trying to scroll to a past time, scroll back to current time
          if (slotTimeInMinutes < currentTimeInMinutes) {
            const currentTimeSlot = document.querySelector(`[data-time="${currentHour.toString().padStart(2, '0')}:${Math.floor(currentMinute / 30) * 30}"]`);
            if (currentTimeSlot) {
              currentTimeSlot.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          }
        }
      }
    };

    const gridContainer = document.querySelector('.scheduler-grid-container');
    if (gridContainer) {
      gridContainer.addEventListener('scroll', handleScroll);
      return () => gridContainer.removeEventListener('scroll', handleScroll);
    }
  }, []);

  // Add current time indicator
  useEffect(() => {
    const updateCurrentTimeIndicator = () => {
      const now = new Date();
      const pstOffset = -7; // PST/PDT offset from UTC
      const utc = now.getTime() + (now.getTimezoneOffset() * 60000);
      const pstTime = new Date(utc + (3600000 * pstOffset));
      
      const currentHour = pstTime.getHours();
      const currentMinute = pstTime.getMinutes();
      const roundedMinute = Math.floor(currentMinute / 30) * 30;
      
      // Remove previous current time indicators
      document.querySelectorAll('.current-time-indicator').forEach(el => el.remove());
      
      // Add new current time indicator
      const timeSlot = document.querySelector(`[data-time="${currentHour.toString().padStart(2, '0')}:${roundedMinute.toString().padStart(2, '0')}"]`);
      if (timeSlot) {
        const indicator = document.createElement('div');
        indicator.className = 'current-time-indicator';
        indicator.style.position = 'absolute';
        indicator.style.left = '0';
        indicator.style.right = '0';
        indicator.style.height = '2px';
        indicator.style.backgroundColor = '#ef4444';
        indicator.style.zIndex = '30';
        timeSlot.appendChild(indicator);
      }
    };

    updateCurrentTimeIndicator();
    const interval = setInterval(updateCurrentTimeIndicator, 60000); // Update every minute
    return () => clearInterval(interval);
  }, []);

  // Function to generate calendar days
  const calendarDays = useMemo(() => {
    const start = startOfMonth(selectedDate);
    const end = endOfMonth(selectedDate);
    return eachDayOfInterval({ start, end });
  }, [selectedDate]);

  // Get day names
  const dayNames = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

  // Function to handle date selection
  const handleDateSelect = (date: Date) => {
    // Set time to midnight to avoid timezone issues
    const normalizedDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    setSelectedDate(normalizedDate);
    setShowCalendar(false);
  };

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Use React Query for notifications
  const { data: notificationsRaw = [] } = useQuery<Notification[]>({
    queryKey: ['notifications', businessId, facilityId],
    queryFn: async () => queryClient.getQueryData<Notification[]>(['notifications', businessId, facilityId]) || [],
  });
  const notifications = notificationsRaw.map(n => ({
    ...n,
    timestamp: typeof n.timestamp === 'string' ? new Date(n.timestamp) : n.timestamp,
  }));
  const addNotification = (message: string) => {
    const id = Math.random().toString(36).substr(2, 9);
    const newNotification = { id, message, timestamp: new Date() };
    queryClient.setQueryData(['notifications', businessId, facilityId], (prev: Notification[] = []) => [...prev, newNotification]);
  };
  const dismissNotification = (id: string) => {
    queryClient.setQueryData(['notifications', businessId, facilityId], (prev: Notification[] = []) => {
      const filtered = prev.filter(n => n.id !== id);
      return filtered;
    });
  };

  // Add debug log for NotificationCounter props
  console.log('NotificationCounter notifications:', notifications);

  if (isMobile && !forceShowGrid) {
    return (
      <div className="p-4">
        {showSmartModal && (
          <NewCourtReservationModal
            open={true}
            onClose={() => setShowSmartModal(false)}
            date={selectedDate}
            courts={courts}
            assignableUsers={[{
              id: user?.uid || '',
              name: userName || '',
              email: user?.email || '',
              role: 'member',
              type: 'member'
            }]}
            selectedUser={user?.uid || ''}
            onSave={async () => {
              await refetchSchedules();
              setShowSmartModal(false);
            }}
            recommendedTimeSlots={courts.length > 0 ? getRecommendedTimeSlots(courts[0].id) : []}
            selectedDuration={selectedDuration}
            onDurationChange={setSelectedDuration}
            businessHours={businessHours}
            showAssignTo={false}
            businessId={businessId}
          />
        )}
        {!showSmartModal && (
          <button
            className="button button-primary mt-8"
            onClick={() => setShowSmartModal(true)}
          >
            Book a Court
          </button>
        )}
      </div>
    );
  }

  if (courtsLoading || schedulesLoading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="court-grid-scheduler flex-1 flex flex-col">
      <NotificationCounter 
        notifications={notifications}
        onDismiss={dismissNotification}
        currentUserId={user?.uid}
      />
      <div className="scheduler-header flex-shrink-0">
        <div className="scheduler-date-navigation">
          <button onClick={() => setSelectedDate(subDays(selectedDate, 1))}>
            Previous Day
          </button>
          <div className="date-selector">
            <h2 onClick={() => setShowCalendar(!showCalendar)}>
              {format(selectedDate, 'MMMM d, yyyy')}
            </h2>
            {showCalendar && (
              <div className="calendar-popup">
                <div className="calendar-header">
                  <h3>{format(selectedDate, 'MMMM, yyyy')}</h3>
                </div>
                <div className="calendar-grid">
                  {/* Day names */}
                  {dayNames.map(day => (
                    <div key={day} className="calendar-day-name">
                      {day}
                    </div>
                  ))}
                  
                  {/* Empty cells for days before the first of the month */}
                  {Array.from({ length: new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1).getDay() }).map((_, index) => (
                    <div key={`empty-${index}`} className="calendar-day empty" />
                  ))}
                  
                  {/* Calendar days */}
                  {calendarDays.map(day => (
                    <div
                      key={day.toISOString()}
                      className={`calendar-day ${
                        format(day, 'yyyy-MM-dd') === format(selectedDate, 'yyyy-MM-dd')
                          ? 'selected'
                          : ''
                      }`}
                      onClick={() => handleDateSelect(day)}
                    >
                      {format(day, 'd')}
                    </div>
                  ))}
                </div>
                <div className="calendar-footer">
                  <button 
                    className="today-button"
                    onClick={() => handleDateSelect(new Date())}
                  >
                    Today
                  </button>
                </div>
              </div>
            )}
          </div>
          <button onClick={() => setSelectedDate(addDays(selectedDate, 1))}>
            Next Day
          </button>
        </div>
        <div className="scheduler-actions">
          {!disableNewReservation && (
            <button 
              className="button button-primary"
              onClick={() => setShowNewReservationModal(true)}
            >
              New Reservation
            </button>
          )}
        </div>
      </div>

      <div className="scheduler-grid-container flex-1 overflow-auto">
        <div className="scheduler-grid">
          <div className="scheduler-time-column">
            <div className="scheduler-time-header"></div>
            {TIME_SLOTS.map(time => (
              <div 
                key={time.value} 
                className="scheduler-time-slot"
                data-time={time.value}
              >
                {time.display}
              </div>
            ))}
            {/* Closed row time label */}
            {(() => {
              const dayOfWeek = selectedDate.getDay();
              const dayName = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'][dayOfWeek];
              const hours = businessHours[dayName];
              if (!hours.isClosed) {
                const [closeHour, closeMinute] = hours.closeTime.split(':').map(Number);
                const closingPeriod = closeHour >= 12 ? 'PM' : 'AM';
                const closingDisplayHour = closeHour > 12 ? closeHour - 12 : closeHour === 0 ? 12 : closeHour;
                const closingDisplay = `${closingDisplayHour}:${closeMinute.toString().padStart(2, '0')} ${closingPeriod}`;
                return (
                  <div className="scheduler-time-slot text-gray-400 font-semibold italic" style={{ background: '#f3f4f6' }}>
                    {closingDisplay}
                  </div>
                );
              }
              return null;
            })()}
          </div>

          <div className="scheduler-courts">
            {courts.map(court => (
              <div key={court.id} className="scheduler-court-column">
                <div className="scheduler-court-header">
                  <h3>{court.name}</h3>
                  <div className="scheduler-court-type">{court.type}</div>
                </div>
                {TIME_SLOTS.map(time => {
                  const { status, details, reservation, isStart, isMiddle, isEnd, isEvent, isRecurring, isOwnReservation } = getSlotStatus(court.id, time.value);
                  const slotClasses = [
                    'scheduler-slot',
                    status,
                    isStart ? 'slot-start' : '',
                    isMiddle ? 'slot-middle' : '',
                    isEnd ? 'slot-end' : '',
                    isEvent ? 'event-slot' : '',
                    isRecurring ? 'recurring-slot' : '',
                    isOwnReservation ? 'own-reservation' : '',
                    // Add current time indicator
                    time.value === format(new Date(), 'HH:mm') ? 'current-time' : '',
                    reservation?.className
                  ].filter(Boolean).join(' ');

                  return (
                    <div
                      key={`${court.id}-${time.value}`}
                      className={slotClasses}
                      onClick={() => !isEvent && handleSlotClick(court.id, time.value, reservation || undefined)}
                      data-time={time.value}
                      style={reservation && (reservation as any).isClass ? { backgroundColor: '#FFD580', opacity: 0.8 } : undefined}
                    >
                      {isStart && details && (
                        <div className="scheduler-slot-details">
                          <div className="scheduler-slot-details-name flex items-center gap-2 font-semibold">
                            <span>{details.name}</span>
                            {isRecurring && (
                              <span className="recurring-indicator ml-1" title="Recurring Event">↻</span>
                            )}
                          </div>
                          <div className="scheduler-slot-details-time">{details.time}</div>
                        </div>
                      )}
                    </div>
                  );
                })}
                {/* Closed row slot */}
                {(() => {
                  const dayOfWeek = selectedDate.getDay();
                  const dayName = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'][dayOfWeek];
                  const hours = businessHours[dayName];
                  if (!hours.isClosed) {
                    return (
                      <div
                        className="scheduler-slot blocked closed-slot flex items-center justify-center text-gray-400 font-semibold"
                        style={{ background: '#f3f4f6', color: '#9ca3af', fontStyle: 'italic' }}
                      >
                        Closed
                      </div>
                    );
                  }
                  return null;
                })()}
              </div>
            ))}
          </div>
        </div>
      </div>

      {showNewReservationModal && (
        <NewCourtReservationModal
          open={showNewReservationModal}
          onClose={() => setShowNewReservationModal(false)}
          date={selectedDate}
          courts={courts}
          courtId={selectedTimeSlot?.courtId}
          startTime={selectedTimeSlot?.time}
          endTime={selectedTimeSlot?.time ? format(
            new Date(selectedDate.setHours(
              parseInt(selectedTimeSlot.time.split(':')[0]) + 1,
              parseInt(selectedTimeSlot.time.split(':')[1])
            )),
            'HH:mm'
          ) : undefined}
          assignableUsers={[{
            id: user?.uid || '',
            name: userName || '',
            email: user?.email || '',
            role: 'member',
            type: 'member'
          }]}
          selectedUser={user?.uid || ''}
          onSave={async () => {
            await refetchSchedules();
            setShowNewReservationModal(false);
          }}
          recommendedTimeSlots={courts.length > 0 ? getRecommendedTimeSlots(courts[0].id) : []}
          selectedDuration={selectedDuration}
          onDurationChange={setSelectedDuration}
          businessHours={businessHours}
          showAssignTo={false}
          businessId={businessId}
        />
      )}

      {isModalOpen && (selectedTimeSlot || selectedSchedule) && (
        <NewCourtReservationModal
          open={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            setSelectedSchedule(null);
            setSelectedTimeSlot(null);
          }}
          courtId={selectedSchedule?.courtId || selectedTimeSlot!.courtId}
          date={selectedDate}
          startTime={selectedSchedule?.startTime || selectedTimeSlot!.time}
          endTime={selectedSchedule?.endTime || format(
            new Date(selectedDate.setHours(
              parseInt(selectedTimeSlot!.time.split(':')[0]) + 1,
              parseInt(selectedTimeSlot!.time.split(':')[1])
            )),
            'HH:mm'
          )}
          courts={courts}
          assignableUsers={[{
            id: user?.uid || '',
            name: userName || '',
            email: user?.email || '',
            role: 'member',
            type: 'member'
          }]}
          selectedUser={user?.uid || ''}
          existingReservation={selectedSchedule}
          onSave={async () => {
            await refetchSchedules();
            setIsModalOpen(false);
            setSelectedSchedule(null);
            setSelectedTimeSlot(null);
          }}
          recommendedTimeSlots={courts.length > 0 ? getRecommendedTimeSlots(courts[0].id) : []}
          selectedDuration={selectedDuration}
          onDurationChange={setSelectedDuration}
          businessHours={businessHours}
          showAssignTo={false}
          businessId={businessId}
        />
      )}
    </div>
  );
};

export default NewMemberCourtReservations; 